import { useState } from "react";
import { Box, Button, Chip, Paper, Popover, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import {
  FORECAST_COLOR,
  HISTORICAL_COLOR,
  SCADA_COLOR,
  forecastColor,
  formatRunInterval,
  formatRunTimestamp,
} from "./pvGenerationUtils";

function LineSwatch({ color, dashed }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 0,
        borderTop: `3px ${dashed ? "dashed" : "solid"} ${color}`,
      }}
    />
  );
}

function RunCard({ title, run, color, dashed, onRemoveRun, timeZone }) {
  return (
    <Paper
      variant="outlined"
      sx={{ py: 0.75, px: 1.5, borderRadius: 2, flex: 1, minWidth: 200, borderTop: `3px solid ${color}` }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <LineSwatch color={color} dashed={dashed} />
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {title}
        </Typography>
      </Stack>
      {run ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
          <Chip
            size="small"
            label={`${formatRunTimestamp(run.created_at, timeZone)} · ${formatRunInterval(run)}`}
            onDelete={onRemoveRun ? () => onRemoveRun(run.run_id) : undefined}
            variant="outlined"
            sx={{ borderColor: color, fontSize: "0.7rem" }}
          />
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
          No completed run selected yet.
        </Typography>
      )}
    </Paper>
  );
}

function ForecastRunsCard({ forecastRuns, onRemoveForecastRun, timeZone }) {
  return (
    <Paper
      variant="outlined"
      sx={{ py: 0.75, px: 1.5, borderRadius: 2, flex: 1, minWidth: 200, borderTop: `3px solid ${FORECAST_COLOR}` }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <LineSwatch color={FORECAST_COLOR} dashed />
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Forecast-weather run{forecastRuns.length !== 1 ? "s" : ""}
        </Typography>
      </Stack>
      {forecastRuns.length === 0 ? (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
          No completed run selected yet.
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
          {forecastRuns.map((run, idx) => (
            <Chip
              key={run.run_id}
              size="small"
              label={`${formatRunTimestamp(run.created_at, timeZone)} · ${formatRunInterval(run)}`}
              onDelete={() => onRemoveForecastRun(run.run_id)}
              variant="outlined"
              sx={{ borderColor: forecastColor(idx), fontSize: "0.7rem" }}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export default function PvComparisonSummary({
  historicalRun,
  forecastRuns,
  onRemoveForecastRun,
  measuredAvailable,
  measuredLoading,
  measuredError,
  timeZone,
  showMeasured = true,
}) {
  const [infoAnchor, setInfoAnchor] = useState(null);

  return (
    <Stack spacing={1}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.04em" }}>
        CURRENT COMPARISON
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
        <RunCard
          title="Historical-weather run"
          run={historicalRun}
          color={HISTORICAL_COLOR}
          dashed={false}
          onRemoveRun={onRemoveForecastRun}
          timeZone={timeZone}
        />
        <ForecastRunsCard
          forecastRuns={forecastRuns}
          onRemoveForecastRun={onRemoveForecastRun}
          timeZone={timeZone}
        />
      </Stack>

      {showMeasured && (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          SCADA comparison:
        </Typography>
        {measuredLoading ? (
          <CircularProgress size={14} />
        ) : (
          <Chip
            size="small"
            label={measuredAvailable ? "Connected" : "Not connected"}
            color={measuredAvailable ? "success" : "default"}
            variant={measuredAvailable ? "filled" : "outlined"}
            sx={measuredAvailable ? { bgcolor: SCADA_COLOR, color: "#fff" } : undefined}
          />
        )}
        {measuredError && (
          <Typography variant="caption" sx={{ color: "error.main" }}>
            {measuredError}
          </Typography>
        )}
        {!measuredAvailable && !measuredLoading && (
          <>
            <Button
              size="small"
              variant="text"
              disabled
              sx={{ textTransform: "none", fontWeight: 700, minWidth: 0, py: 0, fontSize: "0.75rem" }}
            >
              Connect SCADA
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => setInfoAnchor(e.currentTarget)}
              sx={{ textTransform: "none", fontWeight: 700, minWidth: 0, py: 0, fontSize: "0.75rem" }}
            >
              Learn more
            </Button>
            <Popover
              open={Boolean(infoAnchor)}
              anchorEl={infoAnchor}
              onClose={() => setInfoAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Box sx={{ p: 2, maxWidth: 320 }}>
                <Typography variant="body2">
                  SCADA integration lets measured generation data be compared directly against
                  forecast and historical-weather estimates, including MAE, RMSE, bias, and
                  forecast accuracy. This project does not have a SCADA connection configured
                  yet — contact your account team to set one up.
                </Typography>
              </Box>
            </Popover>
          </>
        )}
      </Stack>
      )}
    </Stack>
  );
}
